import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro, MONTHS_EL_SHORT } from "@/lib/format";
import { PLIgnoredExpenses } from "@/components/PLIgnoredExpenses";
import { toast } from "@/components/ui/sonner";
import { TrendingUp, Save, Wand2, ChevronRight, ChevronDown } from "lucide-react";
import { AddLineButton } from "@/components/AddLineButton";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const COLS = MONTH_KEYS.length + 5;

const SectionBar = ({ label, hint }) => (
  <tr className="bg-[#45423C]/5">
    <td colSpan={COLS} className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[#45423C]">
      {label}{hint && <span className="ml-2 font-normal normal-case tracking-normal text-[#6F6A60]">{hint}</span>}
    </td>
  </tr>
);

function Line({ code, label, months, total, budget, variance, forecast, indent, strong, dark, testid, muted, budgetCell, onToggle, open }) {
  const bg = dark ? "#45423C" : strong ? "#F1EDE6" : "#FFFFFF";
  return (
    <tr data-testid={testid} className={`border-t border-[#E3DED4] ${dark ? "text-white border-[#45423C]" : ""} ${strong || dark ? "font-semibold" : ""} hover:bg-[#F7F5F1]/60`} style={{ background: bg }}>
      <td className={`px-3 py-2 sticky left-0 max-w-[320px] truncate ${indent ? "pl-7" : ""}`} style={{ background: bg }} title={label}>
        {onToggle ? (
          <button data-testid={`${testid}-toggle`} onClick={onToggle} className="inline-flex items-center gap-1.5 text-left hover:underline">
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {code && <span className="font-mono text-[10px] text-[#6F6A60]">{code}</span>}
            {label}
          </button>
        ) : (
          <>
            {code && <span className={`font-mono text-[10px] mr-2 ${dark ? "text-white/60" : "text-[#6F6A60]"}`}>{code}</span>}
            {label}
          </>
        )}
      </td>
      {MONTH_KEYS.map((m) => (
        <td key={m} className={`px-2 py-2 text-right tabular ${(months?.[m] || 0) ? "" : dark ? "text-white/40" : "text-[#D8D0C4]"}`}>
          {(months?.[m] || 0) ? fmtEuro(months[m]) : "—"}
        </td>
      ))}
      <td className={`px-3 py-2 text-right tabular font-semibold ${dark ? "" : "bg-[#F7F5F1]"}`}>{fmtEuro(total || 0)}</td>
      <td className="px-2 py-1 text-right tabular">{budgetCell ?? (budget ? fmtEuro(budget) : <span className="text-[#D8D0C4]">—</span>)}</td>
      <td className={`px-3 py-2 text-right tabular ${dark ? "" : (variance || 0) < 0 ? "text-[#991B1B] font-semibold" : (variance || 0) > 0 ? "text-[#6E7A63] font-semibold" : "text-[#D8D0C4]"}`}>
        {budget ? fmtEuro(variance || 0) : "—"}
      </td>
      <td className={`px-3 py-2 text-right tabular ${dark ? "" : muted ? "text-[#8A8577]" : "text-[#6F6A60]"}`}>{fmtEuro(forecast || 0)}</td>
    </tr>
  );
}

function SupplierRows({ year, code }) {
  const { data, isLoading } = useQuery({
    queryKey: ["pl-suppliers", year, code],
    queryFn: async () => (await api.get(`/finance/supplier-expenses/by-category?year=${year}&code=${encodeURIComponent(code)}`)).data,
  });
  if (isLoading) {
    return <tr><td colSpan={COLS} className="px-3 py-2 pl-14 text-[#6F6A60]">Φόρτωση προμηθευτών…</td></tr>;
  }
  if (!(data?.rows || []).length) {
    return <tr><td colSpan={COLS} className="px-3 py-2 pl-14 text-[#6F6A60]">Δεν υπάρχουν προμηθευτές σε αυτόν τον κωδικό.</td></tr>;
  }
  return data.rows.map((s) => (
    <tr key={s.id} data-testid={`plo-sup-${s.id}`} className="border-t border-[#F1EDE6] bg-[#FCFBF9] text-[#6F6A60]">
      <td className="px-3 py-1.5 sticky left-0 bg-[#FCFBF9] pl-14 max-w-[320px] truncate" title={s.supplier}>{s.supplier}</td>
      {MONTH_KEYS.map((m) => (
        <td key={m} className={`px-2 py-1.5 text-right tabular ${(s.months?.[m] || 0) ? "" : "text-[#E3DED4]"}`}>
          {(s.months?.[m] || 0) ? fmtEuro(s.months[m]) : "—"}
        </td>
      ))}
      <td className="px-3 py-1.5 text-right tabular bg-[#F7F5F1]">{fmtEuro(s.total || 0)}</td>
      <td /><td /><td />
    </tr>
  ));
}

const PLDocs = () => (
  <div className="rounded-2xl bg-[#FAF8F4] border border-[#E3DED4] p-5 flex flex-wrap items-center justify-between gap-3" data-testid="pl-docs-window">
    <div>
      <h2 className="font-medium text-[#45423C]">Έξοδα SoftOne — ποιο παραστατικό πάει σε ποια γραμμή</h2>
      <p className="text-sm text-[#6F6A60] mt-1">Κατηγορία → προμηθευτής → παραστατικό, γραμμή P&L και «Μετράει». Οι αλλαγές ενημερώνουν αμέσως αυτή τη σελίδα.</p>
    </div>
    <Link to="/expense-categories" data-testid="pl-goto-categories" className="rounded-xl bg-[#45423C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2F2D29]">Κατηγορίες εξόδων →</Link>
  </div>
);

export default function PLReport() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [year, setYear] = useState(2026);
  const [draft, setDraft] = useState({});
  const [openCat, setOpenCat] = useState({});
  const toggleCat = (c) => setOpenCat((p) => ({ ...p, [c]: !p[c] }));
  const canEdit = ["owner", "hr"].includes(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ["pl-own", year],
    queryFn: async () => (await api.get(`/finance/pl-own?year=${year}`)).data,
  });

  useEffect(() => { setDraft({}); }, [year]);

  const allRows = [...(data?.expense_rows || []), ...(data?.below_rows || [])];

  const saveBudget = useMutation({
    mutationFn: async () => (await api.put("/finance/expense-budget", {
      year,
      lines: Object.entries(draft).map(([code, annual]) => ({
        code, annual: Number(annual) || 0,
        label: allRows.find((r) => r.code === code)?.label || code,
      })),
    })).data,
    onSuccess: (d) => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ["pl-own"] });
      toast.success(`Στόχοι αποθηκεύτηκαν — σύνολο ${fmtEuro(d.total)}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const suggest = useMutation({
    mutationFn: async () => (await api.post(`/finance/expense-budget/suggest?year=${year}&source_year=${year - 1}&growth_pct=0`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["pl-own"] });
      toast.success(`Πρόταση από ${d.source_year}: ${d.codes} κωδικοί · ${fmtEuro(d.total)}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const budgetInput = (row) => canEdit ? (
    <input data-testid={`plo-budget-${row.code}`} type="number"
      value={draft[row.code] ?? (row.budget || "")}
      onChange={(e) => setDraft((p) => ({ ...p, [row.code]: e.target.value }))}
      placeholder="0"
      className="w-24 rounded-lg border border-[#E3DED4] bg-white px-2 py-1 text-right text-xs tabular outline-none focus:border-[#45423C]" />
  ) : undefined;

  const margin = data?.income?.total ? Math.round((data.ebitda.total / data.income.total) * 1000) / 10 : null;
  const dirty = Object.keys(draft).length > 0;

  return (
    <div className="space-y-6" data-testid="pl-report-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Profit &amp; Loss · Δικοί μου κωδικοί</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Κατάσταση Αποτελεσμάτων</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Έσοδα καθαρά (χωρίς ΦΠΑ), έξοδα ανά εμπορική κατηγορία, κόστος προσωπικού και στόχος ανά κωδικό με απόκλιση και εκτίμηση έτους.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          {canEdit && (
            <>
              <AddLineButton kind="income" />
              <AddLineButton kind="expense" />
              <button data-testid="plo-suggest-budget" onClick={() => { if (window.confirm(`Να συμπληρωθούν οι στόχοι ${year} από τα πραγματικά του ${year - 1};`)) suggest.mutate(); }}
                disabled={suggest.isPending}
                className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A] disabled:opacity-50">
                <Wand2 className="h-4 w-4" /> Πρόταση από {year - 1}
              </button>
              <button data-testid="plo-save-budget" onClick={() => saveBudget.mutate()} disabled={!dirty || saveBudget.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                <Save className="h-4 w-4" /> {saveBudget.isPending ? "Αποθήκευση…" : "Αποθήκευση στόχων"}
              </button>
            </>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Έτος</label>
            <select data-testid="plo-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
              {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[["income", "Έσοδα", data?.income?.total], ["opex", "Λειτουργικά έξοδα", data?.opex?.total],
          ["payroll", "Μισθοδοσία & ΙΚΑ", data?.payroll?.total], ["ebitda", "EBITDA", data?.ebitda?.total],
          ["margin", "Περιθώριο EBITDA", margin === null ? "—" : `${margin}%`]].map(([k, l, v]) => (
          <div key={k} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`plo-kpi-${k}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{typeof v === "number" ? fmtEuro(v) : (v ?? "—")}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Ανάλυση ανά μήνα {year}</h2>
          <span className="text-xs text-[#6F6A60]">
            Κλειστοί μήνες: {(data?.closed_months || []).map((m) => MONTHS_EL_SHORT[m]).join(", ") || "—"}
            {(data?.open_months || []).length > 0 && ` · Εκτίμηση για: ${data.open_months.map((m) => MONTHS_EL_SHORT[m]).join(", ")}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? <div className="p-10 text-center text-[#6F6A60]">Φόρτωση…</div> : (
            <table className="text-xs whitespace-nowrap w-full">
              <thead>
                <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                  <th className="px-3 py-3 sticky left-0 bg-[#45423C] z-10">Κωδικός / Γραμμή</th>
                  {MONTH_KEYS.map((m) => <th key={m} className="px-2 py-3 text-right">{MONTHS_EL_SHORT[Number(m)]}</th>)}
                  <th className="px-3 py-3 text-right bg-[#56524A]">Πραγμ.</th>
                  <th className="px-3 py-3 text-right bg-[#56524A]">Στόχος</th>
                  <th className="px-3 py-3 text-right bg-[#56524A]">Απόκλιση</th>
                  <th className="px-3 py-3 text-right bg-[#6E7A63] text-white">Εκτίμηση</th>
                </tr>
              </thead>

              <tbody>
                <SectionBar label="Έσοδα" hint="καθαρά, χωρίς ΦΠΑ — παραστατικά Hotelizer & POS + Εκδηλώσεις · στόχος από «Έσοδα: Budget vs Πραγματικά»" />
                {(data?.income?.rows || []).map((r) => (
                  <Line key={r.code} testid={`plo-inc-${r.code}`} code={r.code} label={r.label}
                    months={r.months} total={r.total} budget={r.budget} variance={r.variance} forecast={r.forecast_total} indent />
                ))}
                <Line testid="plo-income-total" label="Σύνολο Εσόδων" months={data?.income?.months}
                  total={data?.income?.total} budget={data?.income?.budget}
                  variance={Math.round(((data?.income?.total || 0) - (data?.income?.budget || 0)) * 100) / 100}
                  forecast={data?.income?.forecast_total} strong />
              </tbody>

              <tbody>
                <SectionBar label="Λειτουργικά έξοδα" hint="ανά εμπορική κατηγορία — γράψε στόχο στη στήλη «Στόχος»" />
                {(data?.expense_rows || []).map((r, i, arr) => (
                  <React.Fragment key={r.code}>
                    {r.group && !r.group.startsWith(`${r.code} `) && (i === 0 || arr[i - 1].group !== r.group) && (
                      <tr data-testid={`plo-group-${r.code}`} className="bg-[#F7F5F1]"><td colSpan={COLS} className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#8A8577]">{r.group}</td></tr>
                    )}
                    <Line testid={`plo-exp-${r.code}`} code={r.code} label={r.label}
                      months={r.months} total={r.total} budget={r.budget} variance={r.variance}
                      forecast={r.forecast_total} indent budgetCell={budgetInput(r)}
                      onToggle={() => toggleCat(r.code)} open={!!openCat[r.code]} />
                    {openCat[r.code] && <SupplierRows year={year} code={r.code} />}
                  </React.Fragment>
                ))}
                <Line testid="plo-opex-total" label="Σύνολο Λειτουργικών Εξόδων" months={data?.opex?.months}
                  total={data?.opex?.total} budget={data?.opex?.budget}
                  variance={Math.round(((data?.opex?.budget || 0) - (data?.opex?.total || 0)) * 100) / 100}
                  forecast={data?.opex?.forecast_total} strong />
              </tbody>

              <tbody>
                <SectionBar label="Κόστος προσωπικού" hint="από τις μισθοδοσίες & το ετήσιο ΙΚΑ" />
                {(data?.payroll?.rows || []).map((r) => (
                  <Line key={r.code} testid={`plo-pay-${r.code}`} code={r.code} label={r.label}
                    months={r.months} total={r.total} forecast={r.forecast_total} indent />
                ))}
                <Line testid="plo-payroll-total" label="Σύνολο Κόστους Προσωπικού (αποδοχές + ΦΜΥ + ΕΦΚΑ)" months={data?.payroll?.months}
                  total={data?.payroll?.total} forecast={data?.payroll?.forecast_total} strong />
                <Line testid="plo-ebitda" label="EBITDA (Έσοδα − Λειτουργικά − Προσωπικό)"
                  months={data?.ebitda?.months} total={data?.ebitda?.total}
                  forecast={data?.ebitda?.forecast_total} dark />
              </tbody>

              <tbody>
                <SectionBar label="Κάτω από το EBITDA" hint="αποσβέσεις/εξοπλισμός (910-930), owner (1000), φόρος εισοδήματος (850)" />
                {(data?.below_rows || []).map((r) => (
                  <React.Fragment key={r.code}>
                    <Line testid={`plo-below-${r.code}`} code={r.code} label={r.label}
                      months={r.months} total={r.total} budget={r.budget} variance={r.variance}
                      forecast={r.forecast_total} indent budgetCell={budgetInput(r)}
                      onToggle={() => toggleCat(r.code)} open={!!openCat[r.code]} />
                    {openCat[r.code] && <SupplierRows year={year} code={r.code} />}
                  </React.Fragment>
                ))}
                <Line testid="plo-below-total" label="Σύνολο κάτω από το EBITDA" months={data?.below?.months}
                  total={data?.below?.total} budget={data?.below?.budget}
                  variance={Math.round(((data?.below?.budget || 0) - (data?.below?.total || 0)) * 100) / 100}
                  forecast={data?.below?.forecast_total} strong />
              </tbody>

              <tfoot>
                <Line testid="plo-net" label="Καθαρό Αποτέλεσμα" months={data?.net?.months}
                  total={data?.net?.total} forecast={data?.net?.forecast_total} dark />
              </tfoot>
            </table>
          )}
        </div>
      </div>
      <PLIgnoredExpenses year={year} />
      <PLDocs />
    </div>
  );
}
